import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, Mail, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EmailsTab({ content, activeEmail, setActiveEmail, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  const [emailImages, setEmailImages] = React.useState({});
  const [generatingImage, setGeneratingImage] = React.useState(null);

  const generateImage = async (email, index) => {
    setGeneratingImage(index);
    try {
      const prompt = `Create a professional email header image for: ${email.subject}. ${email.preview}`;
      
      const response = await axios.post(`${API}/images/generate`, {
        prompt,
        content_type: "email"
      });

      setEmailImages(prev => ({
        ...prev,
        [index]: response.data.image_url
      }));

      toast.success("Image generated!");
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Failed to generate image");
    } finally {
      setGeneratingImage(null);
    }
  };

  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <p className="mb-4">No email content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="Emails" />}
      </div>
    );
  }

  const email = content[activeEmail];

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
      {/* Email Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {content.map((_, index) => (
          <div key={index} className="flex items-center gap-1">
            <button
              onClick={() => setActiveEmail(index)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                index === activeEmail
                  ? "bg-[#00d4aa] text-black"
                  : "bg-white/10 text-white/60 hover:bg-white/20"
              }`}
            >
              Email {index + 1}
            </button>
            {onRegenerateItem && (
              <button
                onClick={() => onRegenerateItem(index)}
                disabled={regeneratingItem === index}
                className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                title={`Regenerate email ${index + 1}`}
              >
                <RefreshCw size={12} className={regeneratingItem === index ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Email Preview */}
      <Card className="card">
        <CardContent className="pt-6">
          {/* Email Chrome */}
          <div className="bg-white/5 rounded-t-lg p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-white/60" />
              <div className="flex-1">
                <div className="text-white/40 text-sm mb-1">Subject</div>
                <div className="font-semibold text-white text-lg">{email.subject}</div>
              </div>
            </div>
          </div>

          {/* Preview Text */}
          <div className="bg-white/5 p-4 border-b border-white/10">
            <div className="text-white/40 text-sm mb-1">Preview Text</div>
            <div className="italic text-white/80">{email.preview}</div>
          </div>

          {/* Generated Image */}
          {emailImages[activeEmail] && (
            <div className="m-4 rounded-lg overflow-hidden">
              <img 
                src={emailImages[activeEmail]} 
                alt={`Email ${activeEmail + 1} Header`}
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          {/* Email Body */}
          <div className="bg-white rounded-lg p-8 m-4">
            <div className="text-gray-900 text-base whitespace-pre-wrap leading-relaxed">
              {email.body}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generate Image Button */}
      <Button
        onClick={() => generateImage(email, activeEmail)}
        disabled={generatingImage === activeEmail}
        variant="outline"
        className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
      >
        {generatingImage === activeEmail ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Generating Header Image...
          </>
        ) : (
          <>
            <ImageIcon className="w-4 h-4 mr-2" />
            Generate Header Image with Gemini
          </>
        )}
      </Button>

      {/* Copy Buttons */}
      <div className="grid md:grid-cols-3 gap-4">
        <Button
          onClick={() => copyToClipboard(email.subject)}
          variant="outline"
          className="w-full"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy Subject
        </Button>
        <Button
          onClick={() => copyToClipboard(email.body)}
          className="w-full bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy Body
        </Button>
        <Button
          onClick={() => copyToClipboard(`Subject: ${email.subject}\n\n${email.body}`)}
          variant="outline"
          className="w-full"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy Full Email
        </Button>
      </div>

      {/* Sequence Info */}
      <div className="p-4 bg-white/5 rounded-lg text-center">
        <p className="text-white/60 text-sm">
          Email {activeEmail + 1} of {content.length} in the sequence
        </p>
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={onRegenerate}
          isRegenerating={isRegenerating}
          contentType="Emails"
        />
      )}
    </div>
  );
}

export default EmailsTab;